import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Heart, Globe, Check, ChevronDown } from "lucide-react";
import { useI18n } from "@/context/Language";

export const Navbar = ({ onDonate }) => {
  const { t, lang, setLang, langs } = useI18n();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const current = langs.find((l) => l.code === lang) || langs[0];

  return (
    <motion.header
      data-testid="site-navbar"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        scrolled ? "bg-sand/90 backdrop-blur-md border-b border-border-soft" : "bg-transparent"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-6 sm:px-8 h-[72px] flex items-center justify-between">
        <a href="/" data-testid="logo-link" className="flex items-center gap-2 group">
          <span className="flex h-8 w-8 items-center justify-center bg-terracotta text-sand">
            <Heart className="h-4 w-4" fill="currentColor" />
          </span>
          <span className="font-serif text-xl tracking-tight text-ink">Venezuela Relief</span>
        </a>

        <div className="flex items-center gap-4 sm:gap-6">
          <a href="#story" data-testid="nav-story-link" className="hidden sm:inline text-sm font-medium text-ink/70 hover:text-ink transition-colors">
            {t.nav.story}
          </a>
          <a href="#gallery" data-testid="nav-gallery-link" className="hidden sm:inline text-sm font-medium text-ink/70 hover:text-ink transition-colors">
            {t.nav.gallery}
          </a>

          <div className="relative">
            <button
              data-testid="lang-toggle"
              onClick={() => setOpen((o) => !o)}
              onBlur={() => setTimeout(() => setOpen(false), 150)}
              className="flex items-center gap-1.5 text-sm font-medium text-ink/70 hover:text-ink transition-colors"
            >
              <Globe className="h-4 w-4" />
              <span className="hidden sm:inline">{current.label}</span>
              <ChevronDown className="h-3.5 w-3.5" />
            </button>
            {open && (
              <div
                data-testid="lang-menu"
                className="absolute right-0 mt-2 w-40 border border-border-soft bg-sand py-1 shadow-lg z-50"
              >
                {langs.map((l) => (
                  <button
                    key={l.code}
                    data-testid={`lang-${l.code}`}
                    onMouseDown={() => setLang(l.code)}
                    className="flex w-full items-center justify-between px-4 py-2 text-sm text-ink hover:bg-sand-2 transition-colors"
                  >
                    {l.label}
                    {l.code === lang && <Check className="h-3.5 w-3.5 text-terracotta" />}
                  </button>
                ))}
              </div>
            )}
          </div>

          <button
            data-testid="nav-donate-button"
            onClick={() => onDonate()}
            className="rounded-full bg-terracotta px-5 py-2.5 text-sm font-semibold text-sand transition-colors hover:bg-terracotta-hover active:scale-[0.98]"
          >
            {t.nav.donate}
          </button>
        </div>
      </nav>
    </motion.header>
  );
};

export default Navbar;
